import { ALL_MODELS, DATA_TYPES, MODEL_STATES } from "../constants";
import { argMax } from "../helpers/DataHelpers";
import { stateTypeF } from "../state";
import { BaseModel } from "./BaseModel";
import { dataSplit, infoType } from "./Dataset";

type modelType = { X: number[][]; y: number[]; k: number; outputType: string };

export class kNearestNeighbours extends BaseModel {
  modelName = ALL_MODELS.knn;
  maxElements = 50000;

  model: modelType;
  modelState = MODEL_STATES.ready;

  constructor(mlDataInfo: infoType) {
    super(mlDataInfo);
    this.initModel();
  }

  initModel() {}

  train(trainData: dataSplit, state: stateTypeF) {
    state.setModelState(
      this.modelName,
      MODEL_STATES.training,
      state.saveState.modelTraining.modelStates
    );
    BaseModel.setModelProgress(0, state, this.modelName, 0);
    this.model = {
      X: trainData.xTrain.slice(0, 500),
      y: trainData.yTrain.slice(0, 500),
      k: this.mlDataInfo.outputType == DATA_TYPES.number ? 10 : 20,
      outputType: this.mlDataInfo.outputType,
    };
    this.isBestModel(trainData.xTest, trainData.yTest);
    BaseModel.setModelProgress(100, state, this.modelName, 0);
    state.setModelState(
      this.modelName,
      MODEL_STATES.trained,
      state.saveState.modelTraining.modelStates
    );
  }

  predict(X: number[][]) {
    let output: number[] = [];

    let probs = this.predictProbs(X);
    if (this.mlDataInfo.outputType == DATA_TYPES.number) {
      for (let i = 0; i < probs.length; i++) {
        output.push(this.averageNeighbours(probs[i]));
      }
    } else if (this.mlDataInfo.outputType == DATA_TYPES.categorical) {
      for (let i = 0; i < probs.length; i++) {
        output.push(argMax(probs[i]));
      }
    }
    return output;
  }

  predictProbs(X: number[][]) {
    let probs: number[][] = [];
    let reduceFunction: Function;
    if (this.mlDataInfo.outputType == DATA_TYPES.number) {
      reduceFunction = this.normalizeWeights.bind(this);
    } else if (this.mlDataInfo.outputType == DATA_TYPES.categorical) {
      reduceFunction = this.weightsToClassProbs.bind(this);
    }

    for (let i = 0; i < X.length; i++) {
      let distances = this.calcDistances(X[i]);
      let weights = this.calcWeights(distances);
      probs.push(reduceFunction(weights));
    }
    return probs;
  }

  weightsToClassProbs(weights: number[]) {
    let topIndexes = this.topWeights(weights);
    topIndexes = topIndexes.slice(0, this.model.k);
    weights = topIndexes.map((val) => weights[val]);
    weights = this.normalizeWeights(weights);
    let classes = topIndexes.map((val) => this.model.y[val]);

    let classProbs = Array(this.mlDataInfo.outputShape).fill(0);
    for (let i = 0; i < classes.length; i++) {
      classProbs[classes[i]] += weights[i];
    }
    return classProbs;
  }

  normalizeWeights(weights: number[]) {
    const norm = weights.reduce((a, b) => a + b, 0);
    weights = weights.map((val) => val / norm);
    return weights;
  }

  calcDistances(x: number[]) {
    const X = this.model.X;
    const nRows = X.length;
    const nCols = X[0].length;

    let distances: number[] = Array(nRows);
    for (let i = 0; i < nRows; i++) {
      let dist = 0;
      for (let j = 0; j < nCols; j++) {
        dist += Math.sqrt((X[i][j] - x[j]) ** 2);
      }
      distances[i] = dist;
    }
    return distances;
  }

  topWeights(weights: number[]) {
    let list = [];
    for (var j = 0; j < weights.length; j++)
      list.push({ weight: weights[j], index: j });
    list = list.sort((a, b) => b.weight - a.weight);

    let indexes: number[] = Array(weights.length)
      .fill(0)
      .map((val, i) => i);
    for (let i = 0; i < indexes.length; i++) {
      indexes[i] = list[i].index;
    }

    return indexes;
  }

  calcWeights(distances: number[]) {
    let weights: number[] = Array(distances.length).fill(0);
    for (let i = 0; i < weights.length; i++) {
      let dist = distances[i];
      if (dist < 1e-5) {
        dist = 1e-5;
      }
      weights[i] = 1 / dist;
    }
    return weights;
  }

  averageNeighbours(weights: number[]) {
    let topIndexes = this.topWeights(weights);

    topIndexes = topIndexes.slice(0, this.model.k);

    let weightsSlice = topIndexes.map((val) => weights[val]);
    weightsSlice = this.normalizeWeights(weightsSlice);
    let values = topIndexes.map(
      (val, i) => weightsSlice[i] * this.model.y[val]
    );
    let output = values.reduce((a, b) => a + b, 0);

    return output;
  }
}
